// articles/DotsGame.js
// Game guide (Oct 4, 2026), in the Nassau guide's format. Written from the live code: the
// Dots card in Games Setup (App.js DotsGamePanel — What counts, Who settles with whom (Oct 6, 2026), Sandies & polies
// marked), the Sandy / Poley row on the hole screen (DotsTapRow) and the engine
// (gameEngines/dots.js). Labels quoted exactly; change them here if they change in App.js.
import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-dots-golf-game');

const FAQS = [
  {
    q: 'What counts as a sandy or a poley?',
    a: 'Whatever your group says. TTO doesn\u2019t check the score or the putts: the player taps it, on the honor system, the same as on the course. Agree on your rule before the round (par or better from a bunker, a one-putt from a set distance, and so on).',
  },
  {
    q: 'Are birdies gross or net?',
    a: 'Gross. A birdie is 1 dot, an eagle 2 and an albatross 3, counted from the actual score. There are no handicap strokes in Dots.',
  },
  {
    q: 'A player marked a dot by mistake.',
    a: 'Tap it again on the same hole to take it off. Once the round is final, the organizer removes it on the Games card (✕ beside the dot) and finalizes again.',
  },
  {
    q: 'Can we play Dots with Captain\u2019s Choice?',
    a: 'No. Birdies come from each player\u2019s own score, and a Captain\u2019s Choice round has one score per team.',
  },
  {
    q: 'Our groups aren\u2019t the same size. Does that matter?',
    a: 'No. Within each group, a threesome settles among its three players and a foursome among its four. Any group size works.',
  },
  {
    q: 'A player shows \u201cnot in a group\u201d.',
    a: 'Within each group, a player settles with the others in his pairing group. Put him in a group on the Pairings page and save; his dots count from every hole he has already played.',
  },
  {
    q: 'Can we count only birdies, or only sandies?',
    a: 'Yes. Tick only the dots you want under What counts. At least one has to count.',
  },
];

export default function DotsGame() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up Dots: Birdies, Sandies and Polies
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        Dots is a side game played by many groups: a dot for every <strong>birdie</strong>,{' '}
        <strong>sandy</strong> and <strong>poley</strong>. Birdies count by themselves from the
        scores; players tap Sandy or Poley on the hole screen. At the end, each player settles the
        difference in dots with the others in his group, or with the whole field.
      </P>

      <StepTracker
        storageKey="tto_guide_dots_game_steps"
        steps={['Add Dots', 'Who\u2019s in', 'Play & mark dots', 'Results & settle']}
        note="Steps 1 and 2 happen before the round; 3 and 4 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Dots needs live scoring.</strong> Every player in it needs his scores entered in
          the app as he plays, on his own phone or by the player keeping his group&rsquo;s card.
        </LI>
        <LI>
          <strong>Dots isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          Decide with your group: which dots count, <strong>what qualifies as a sandy or a
          poley</strong> (TTO doesn&rsquo;t check; the player marks it), whether you settle within each group or across the whole field, and
          what a dot is worth.
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Add Dots" when="Before the round">
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Dots</strong>.
          </LI>
          <LI>
            <strong>Wager:</strong> the amount <strong>per dot</strong>. $0 counts the dots and settles
            no money.
          </LI>
          <LI>
            <strong>What counts:</strong> tick any of
            <UL>
              <LI><strong>Birdies</strong>: from the gross score. A birdie is 1 dot, an eagle 2.</LI>
              <LI><strong>Sandies</strong>: the player taps Sandy on the hole.</LI>
              <LI><strong>Polies</strong>: the player taps Poley on the hole.</LI>
            </UL>
          </LI>
          <LI>
            <strong>Who settles with whom:</strong>
            <UL>
              <LI>
                <Btn>Within each group</Btn> (the default): each player settles the difference in
                dots with the others in his own group on the Pairings page. Threesomes and
                foursomes both work. With no pairings saved, everyone in the game is one group.
              </LI>
              <LI>
                <Btn>Whole field</Btn>: every player in the game settles with every other player,
                whatever group they play in.
              </LI>
            </UL>
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Choose who's in" when="After you save, until the first score">
        <UL>
          <LI>
            A new Dots game starts with nobody in it. After you save, the game shows a{' '}
            <strong>Player Opt-Ins</strong> list with every player on the tee time.
          </LI>
          <LI>
            Click a player&rsquo;s <strong>— Out</strong> to make it <strong>✓ In</strong>, or the
            reverse.
          </LI>
          <LI>
            Players can also tap <Btn>Opt In</Btn> in their own app, until the first score of the
            round is entered. After that only you can add or remove a player.
          </LI>
          <LI>
            Only players who are in earn dots and pay or collect.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={3} title="Play and mark your dots" when="During the round">
        <UL>
          <LI>
            <strong>Birdies</strong> count by themselves when the score goes in. Nothing to tap.
          </LI>
          <LI>
            For a <strong>sandy</strong> or a <strong>poley</strong>, tap it on the hole screen. The
            row reads <strong>DOTS ○ Sandy ○ Poley</strong>; a marked dot turns black with a ✓.
          </LI>
          <LI>
            Tap it again to take it off.
          </LI>
          <LI>
            Keeping the card for your group? There&rsquo;s one row per player, so you mark each
            player&rsquo;s dots.
          </LI>
          <LI>
            Players follow the dots in their app: <strong>🏆 Leaderboard</strong> &rarr;{' '}
            <strong>💰 Games</strong>, for example &ldquo;Dots: 3 (2 birdies, 1 sandy)&rdquo;.
          </LI>
        </UL>
        <Heads>
          <strong>Once the round is final, players can&rsquo;t change their dots.</strong> The hole
          screen says to ask the organizer.
        </Heads>
      </Step>

      <Step n={4} title="Results and settling up" when="When the last card is in">
        <UL>
          <LI>
            The round finalizes itself once every card is complete. You can also click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>.
          </LI>
          <LI>
            Each pair of players that settles (the same group, or anyone in the field) settles the
            difference in their dots × the wager. At $1 a dot, Al 4 dots and Bob 1: Bob pays Al $3.
            The leaderboard shows each group under its own name.
          </LI>
          <LI>
            <strong>Fixing a dot:</strong> on the Games card, under{' '}
            <strong>Sandies &amp; polies marked</strong>, click ✕ beside a dot to remove it, or use{' '}
            <strong>Add:</strong> (player, hole, Sandy or Poley) to add one.
          </LI>
          <LI>
            With money on the game, TTO settles it player to player, every player gets a statement,
            and you can email everyone their results with one click. At $0 the statement shows the
            results only.
          </LI>
          <LI>
            <strong>Something needs fixing after finalize?</strong> Click <Btn>↶ Reopen Round</Btn>{' '}
            in <Btn>🎮 Games</Btn>, make the change, then finalize again so the money follows.
          </LI>
        </UL>
      </Step>

      <H2>Running Dots in an event?</H2>
      <P>
        Set the event up with its format guide (📖 Guides &rarr; Setting up Events / Tournaments).
        At the step where you add the main game, tick <strong>Dots</strong> beside it and set it
        up as in step 1 above.
      </P>
      <H2>What changes once scoring starts</H2>
      <UL>
        <LI>
          <strong>Players can no longer opt in or out themselves</strong> once the first score of the
          round is entered. You still can, from <Btn>🎮 Games</Btn>.
        </LI>
        <LI>
          <strong>Settings can still change</strong>, but change them only if you must: the results
          and any money follow the new settings.
        </LI>
      </UL>

      <FAQ id="dots-game" faqs={FAQS} />

      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
